import type {
  CheckboxState,
  EmailMissingInfoTemplateGroup,
  TemplateChild,
} from '../types/modals'
import type { EmailTemplateResponse } from '../types/api'

export function trimMissingInfoString(value: unknown): string {
  if (value == null) return ''
  return String(value).trim()
}

export function missingInfoChildKey(groupKey: string, childKey: string): string {
  return `${groupKey}.${childKey}`
}

export function resolveAllowPersonalizedNote(value: unknown): boolean {
  return value === undefined ? true : Boolean(value)
}

export function normalizeMissingInfoTemplateGroups(
  template: EmailTemplateResponse,
): EmailMissingInfoTemplateGroup[] {
  const raw = template.checkbox_data
  if (!Array.isArray(raw)) return []

  return raw
    .map((group, groupIndex) => {
      const record = group as {
        child?: unknown
        children?: unknown
        key?: string
        name?: string
        label?: string
      }
      const childrenRaw = Array.isArray(record.child)
        ? record.child
        : Array.isArray(record.children)
          ? record.children
          : []

      const children = childrenRaw.map((child, childIndex) => {
        const childRecord = child as {
          key?: string
          name?: string
          label?: string
          allow_personalized_note?: unknown
        }
        return {
          key: (childRecord.key ?? childRecord.name ?? `${record.key ?? 'group'}_${childIndex}`) as string,
          label: trimMissingInfoString(childRecord.label),
          allowPersonalizedNote: resolveAllowPersonalizedNote(
            childRecord.allow_personalized_note,
          ),
        } satisfies TemplateChild
      })

      return {
        key: (record.key ?? record.name ?? `group_${groupIndex}`) as string,
        label: trimMissingInfoString(record.label),
        children,
      }
    })
    .filter((group) => group.children.length > 0)
}

export function toggleMissingInfoChildSelected(
  state: CheckboxState,
  groupKey: string,
  child: TemplateChild,
): CheckboxState {
  const childKey = missingInfoChildKey(groupKey, child.key)
  const isSelected = !state.selected[childKey]

  if (isSelected) {
    return {
      ...state,
      selected: { ...state.selected, [childKey]: true },
    }
  }

  const { [childKey]: _selected, ...selected } = state.selected
  const { [childKey]: _message, ...personalizedMessages } = state.personalizedMessages
  const { [childKey]: _open, ...personalizedOpen } = state.personalizedOpen

  return {
    selected,
    personalizedMessages,
    personalizedOpen,
  }
}

export function toggleMissingInfoPersonalizedOpen(
  state: CheckboxState,
  groupKey: string,
  childKey: string,
): CheckboxState {
  const key = missingInfoChildKey(groupKey, childKey)
  if (!state.selected[key]) return state

  return {
    ...state,
    personalizedOpen: {
      ...state.personalizedOpen,
      [key]: !state.personalizedOpen[key],
    },
  }
}

export function setMissingInfoPersonalizedMessage(
  state: CheckboxState,
  groupKey: string,
  childKey: string,
  value: string,
): CheckboxState {
  const key = missingInfoChildKey(groupKey, childKey)
  return {
    ...state,
    personalizedMessages: {
      ...state.personalizedMessages,
      [key]: value,
    },
  }
}

/** Angular EmailMissingInfoComponent submit payload for `checkbox_data`. */
export function buildMissingInfoCheckboxPayload(
  groups: EmailMissingInfoTemplateGroup[],
  state: CheckboxState,
): Record<string, { key?: string; label?: string; child: Array<Record<string, unknown>> }> {
  const checkElements: Record<
    string,
    { key?: string; label?: string; child: Array<Record<string, unknown>> }
  > = {}

  for (const group of groups) {
    for (const child of group.children) {
      const childKey = missingInfoChildKey(group.key, child.key)
      if (!state.selected[childKey]) continue

      if (!checkElements[group.key]) {
        checkElements[group.key] = {
          key: group.key,
          label: trimMissingInfoString(group.label),
          child: [],
        }
      }

      const data: Record<string, unknown> = {
        key: child.key,
        label: trimMissingInfoString(child.label),
      }

      const personalizedMessage = trimMissingInfoString(
        state.personalizedMessages[childKey],
      )
      if (child.allowPersonalizedNote && personalizedMessage) {
        data.personalized_message = personalizedMessage
      }

      checkElements[group.key].child.push(data)
    }
  }

  return checkElements
}
